import { localize } from "@/i18n/translate";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errorCode } from "@/lib/api";
import { useAuth } from "../AuthContext";
import { usePortal } from "../i18n";
import { Badge, ErrorNote, FormField, PageTitle, btnPrimary, btnSecondary, inputCls } from "../ui";
const Saved = ({
  children
}) => children ? <p role="status" className="text-sm text-ink" data-testid="profile-saved">{localize(children)}</p> : null;
const ProfileForm = () => {
  const {
    user,
    setUser
  } = useAuth();
  const {
    p,
    errText
  } = usePortal();
  const [v, setV] = useState({
    name: user.name || "",
    organisation: user.organisation || "",
    phone: user.phone || ""
  });
  const [state, setState] = useState({});
  const submit = async e => {
    e.preventDefault();
    if (v.name.trim().length < 2) return setState({
      err: p.auth.err.name
    });
    setState({
      busy: true
    });
    try {
      const {
        data
      } = await api.put("/portal/profile", v);
      setUser(data);
      setState({
        ok: p.profile.saved
      });
    } catch (ex) {
      setState({
        err: errText(errorCode(ex))
      });
    }
  };
  return <form onSubmit={submit} noValidate className="space-y-6" data-testid="profile-form">
      <h2 className="font-display text-2xl font-bold tracking-[-0.02em]">{localize(p.profile.data)}</h2>
      <ErrorNote>{localize(state.err)}</ErrorNote>
      <FormField id="pf-name" label={p.auth.name}><input id="pf-name" value={v.name} onChange={e => setV({
        ...v,
        name: e.target.value
      })} className={inputCls(false)} data-testid="profile-name-input" /></FormField>
      <FormField id="pf-org" label={p.auth.org} hint={p.auth.optional}><input id="pf-org" value={v.organisation} onChange={e => setV({
        ...v,
        organisation: e.target.value
      })} className={inputCls(false)} data-testid="profile-org-input" /></FormField>
      <FormField id="pf-phone" label={p.auth.phone} hint={p.auth.optional}><input id="pf-phone" type="tel" value={v.phone} onChange={e => setV({
        ...v,
        phone: e.target.value
      })} className={inputCls(false)} data-testid="profile-phone-input" /></FormField>
      <div className="flex items-center gap-4">
        <button type="submit" disabled={state.busy} className={btnPrimary} data-testid="profile-save-btn">{localize(state.busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />)} {localize(p.profile.save)}</button>
        <Saved>{localize(state.ok)}</Saved>
      </div>
    </form>;
};
const PasswordForm = () => {
  const {
    p,
    errText
  } = usePortal();
  const [v, setV] = useState({
    current_password: "",
    new_password: ""
  });
  const [state, setState] = useState({});
  const submit = async e => {
    e.preventDefault();
    if (!v.current_password) return setState({
      err: p.auth.err.passwordEmpty
    });
    if (v.new_password.length < 8) return setState({
      err: p.auth.err.password
    });
    setState({
      busy: true
    });
    try {
      await api.put("/portal/password", v);
      setV({
        current_password: "",
        new_password: ""
      });
      setState({
        ok: p.profile.pwSaved
      });
    } catch (ex) {
      setState({
        err: errText(errorCode(ex))
      });
    }
  };
  return <form onSubmit={submit} noValidate className="space-y-6" data-testid="password-form">
      <h2 className="font-display text-2xl font-bold tracking-[-0.02em]">{localize(p.profile.pw)}</h2>
      <ErrorNote testId="password-error">{localize(state.err)}</ErrorNote>
      <FormField id="pw-current" label={p.profile.current}><input id="pw-current" type="password" autoComplete="current-password" value={v.current_password} onChange={e => setV({
        ...v,
        current_password: e.target.value
      })} className={inputCls(false)} data-testid="password-current-input" /></FormField>
      <FormField id="pw-new" label={p.profile.newPw} hint={p.auth.passwordHint}><input id="pw-new" type="password" autoComplete="new-password" value={v.new_password} onChange={e => setV({
        ...v,
        new_password: e.target.value
      })} className={inputCls(false)} data-testid="password-new-input" /></FormField>
      <div className="flex items-center gap-4">
        <button type="submit" disabled={state.busy} className={btnSecondary} data-testid="password-save-btn">{localize(p.profile.pwSave)}</button>
        <Saved>{localize(state.ok)}</Saved>
      </div>
    </form>;
};
export default function Profile() {
  const {
    user
  } = useAuth();
  const {
    p,
    fmtDate
  } = usePortal();
  return <div data-testid="profile-page">
      <PageTitle eyebrow={p.area} title={localize(p.profile.title)} />
      <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_280px]">
        <div className="max-w-xl space-y-14">
          <ProfileForm />
          <PasswordForm />
        </div>
        <aside className="h-fit border border-ink/15 bg-white/60 p-6 text-sm">
          <p className="eyebrow">{localize(p.profile.account)}</p>
          <p className="mt-3 break-all font-medium">{localize(user.email)}</p>
          <div className="mt-3"><Badge kind="user" value={user.status} label={p.userStatus[user.status]} /></div>
          <p className="mt-4 text-mute">{localize(p.profile.since)} {localize(fmtDate(user.created_at))}</p>
        </aside>
      </div>
    </div>;
}
